import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Retained committed versions: the undo depth ceiling. */
export const VERSION_HISTORY_LIMIT = 100;

/** One step through the history: the version left, and the one to show now. */
export interface VersionStep<Version> {
  from: Version;
  to: Version;
}

export interface VersionHistoryOptions<Version> {
  /** False while a drag is in flight: `shown` is then not a version yet. */
  committed: boolean;
  /** Whether two versions would save the same. */
  same: (version: Version, other: Version) => boolean;
  /** What the host has persisted, when that differs from what is first shown (rooms the floor plan view placed itself). */
  saved?: Version;
}

export interface VersionHistory<Version> {
  canUndo: boolean;
  canRedo: boolean;
  /** The version shown is not the saved one, and it would save differently. */
  isDirty: boolean;
  /** The version the host persisted last. */
  saved: Version;
  /** Step back a version; null when there is nothing to undo. The caller shows `to`. */
  undo: () => VersionStep<Version> | null;
  /** Step forward again after an undo; null when there is nothing to redo. */
  redo: () => VersionStep<Version> | null;
  /** The version shown now is saved. */
  markSaved: () => void;
  /**
   * The host sent a new version (a refresh): it takes the place of the version
   * shown, with no undo step of its own, and the host has persisted `saved`.
   * Every other version undo and redo can reach is passed through `rebase`, so
   * stepping never brings back what the refresh replaced, and a version that
   * then saves the same as its neighbor is dropped, so no step does nothing.
   * The caller shows `version` next.
   */
  replaceShown: (version: Version, saved: Version, rebase: (kept: Version) => Version) => void;
}

/**
 * The undo history and the unsaved-changes rule, one implementation for both
 * editors (the room editor's room, the floor plan view's room list).
 *
 * Each committed change records a version, after render, so the state
 * updaters stay pure under StrictMode. Undo and redo step through the
 * versions, and a change made after an undo drops the versions it undid. A
 * change committed on the render that reads the history counts toward
 * `canUndo` before its effect records it. Saving points the saved marker at
 * the version shown.
 *
 * The document is dirty exactly when the version shown is not the saved one
 * and it would save differently (`same`): undoing back onto the saved version
 * makes it clean again, an edit that changes nothing that saves stays clean,
 * and a change made after undoing past the saved version leaves it
 * unreachable, so the document stays dirty until the next save.
 */
export function useVersionHistory<Version>(
  shown: Version,
  { committed, same, saved: initialSaved = shown }: VersionHistoryOptions<Version>,
): VersionHistory<Version> {
  const versionsRef = useRef<Version[]>([shown]);
  const redoRef = useRef<Version[]>([]);
  const lastCommittedRef = useRef(shown);
  const shownRef = useRef(shown);
  shownRef.current = shown;
  const sameRef = useRef(same);
  sameRef.current = same;
  const [saved, setSaved] = useState(initialSaved);

  useEffect(() => {
    if (!committed || shown === lastCommittedRef.current) return;
    lastCommittedRef.current = shown;
    recordVersion(versionsRef.current, redoRef.current, shown);
  }, [shown, committed]);

  const undo = useCallback((): VersionStep<Version> | null => {
    const versions = versionsRef.current;
    if (versions.length < 2) return null;
    const from = versions.pop()!;
    redoRef.current.push(from);
    return { from, to: versions[versions.length - 1] };
  }, []);

  const redo = useCallback((): VersionStep<Version> | null => {
    const to = redoRef.current.pop();
    if (to === undefined) return null;
    const from = versionsRef.current[versionsRef.current.length - 1];
    versionsRef.current.push(to);
    return { from, to };
  }, []);

  const markSaved = useCallback(() => setSaved(shownRef.current), []);

  const replaceShown = useCallback((version: Version, persisted: Version, rebase: (kept: Version) => Version) => {
    const undone = withoutRepeats([...versionsRef.current.slice(0, -1).map(rebase), version].reverse(), sameRef.current).reverse();
    const redone = withoutRepeats([version, ...[...redoRef.current].reverse().map(rebase)], sameRef.current).slice(1);
    versionsRef.current = undone;
    redoRef.current = redone.reverse();
    lastCommittedRef.current = version;
    setSaved(persisted);
  }, []);

  const isDirty = useMemo(() => shown !== saved && !sameRef.current(shown, saved), [shown, saved]);
  const pendingCommit = committed && shown !== versionsRef.current[versionsRef.current.length - 1];
  return {
    canUndo: versionsRef.current.length > 1 || pendingCommit,
    canRedo: redoRef.current.length > 0 && !pendingCommit,
    isDirty,
    saved,
    undo,
    redo,
    markSaved,
    replaceShown,
  };
}

/** `versions` in order with each run of versions that save the same kept to its first. */
const withoutRepeats = <Version>(versions: Version[], same: (version: Version, other: Version) => boolean): Version[] =>
  versions.filter((version, index) => index === 0 || !same(versions[index - 1], version));

/** Record `version` after the current one: a change made after an undo drops the undone branch. Undo and redo already put their target in place. */
function recordVersion<Version>(versions: Version[], redone: Version[], version: Version): void {
  if (versions[versions.length - 1] === version) return;
  versions.push(version);
  if (versions.length > VERSION_HISTORY_LIMIT) versions.shift();
  redone.length = 0;
}
